// Enhanced Question Transitions - Advanced framer-motion animations for adaptive questions
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const EnhancedQuestionTransitions = ({ 
  children, 
  questionId, 
  difficulty = 'medium',
  isFollowUp = false,
  isAdaptive = false,
  transitionType = 'default'
}) => {
  // Transition variants based on question type
  const getTransitionVariants = () => {
    switch (transitionType) {
      case 'difficulty-shift':
        return {
          initial: { 
            opacity: 0, 
            scale: difficulty === 'hard' ? 1.1 : 0.9,
            rotateX: difficulty === 'hard' ? -15 : 15
          },
          animate: { 
            opacity: 1, 
            scale: 1,
            rotateX: 0
          },
          exit: { 
            opacity: 0, 
            scale: difficulty === 'hard' ? 0.9 : 1.1,
            rotateX: difficulty === 'hard' ? 15 : -15
          }
        };
      
      case 'follow-up':
        return {
          initial: { 
            opacity: 0, 
            x: 50,
            scale: 0.95
          },
          animate: { 
            opacity: 1, 
            x: 0,
            scale: 1
          },
          exit: { 
            opacity: 0, 
            x: -50,
            scale: 0.95
          }
        };
      
      case 'adaptive':
        return {
          initial: { 
            opacity: 0, 
            y: 30,
            rotateY: -10
          },
          animate: { 
            opacity: 1, 
            y: 0,
            rotateY: 0
          },
          exit: { 
            opacity: 0, 
            y: -30,
            rotateY: 10
          }
        };
      
      default:
        return {
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: -20 }
        };
    }
  };

  const variants = getTransitionVariants();

  // Dynamic transition timing based on question type
  const getTransitionConfig = () => {
    if (isFollowUp) {
      return {
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.6
      };
    }
    
    if (difficulty === 'hard') {
      return {
        type: "spring",
        stiffness: 200,
        damping: 25,
        duration: 0.8
      };
    }
    
    return {
      type: "spring",
      stiffness: 400,
      damping: 40,
      duration: 0.4
    };
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={questionId}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={getTransitionConfig()}
        style={{ perspective: 1000 }}
        className="w-full"
      >
        {/* Difficulty-based background animation */}
        {difficulty === 'hard' && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl -z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          />
        )}
        
        {/* Follow-up question highlight */}
        {isFollowUp && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl -z-10"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 0.4, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4 }}
          />
        )}
        
        {/* Adaptive selection glow */}
        {isAdaptive && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl -z-10"
            animate={{
              opacity: [0.2, 0.4, 0.2],
              scale: [1, 1.01, 1]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        )}
        
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// Specialized transition components
export const DifficultyShiftTransition = ({ children, questionId, difficulty }) => (
  <EnhancedQuestionTransitions
    questionId={questionId}
    difficulty={difficulty}
    transitionType="difficulty-shift"
  >
    {children}
  </EnhancedQuestionTransitions>
);

export const FollowUpTransition = ({ children, questionId }) => (
  <EnhancedQuestionTransitions
    questionId={questionId}
    isFollowUp={true}
    transitionType="follow-up"
  >
    {children}
  </EnhancedQuestionTransitions>
);

export const AdaptiveTransition = ({ children, questionId }) => (
  <EnhancedQuestionTransitions
    questionId={questionId}
    isAdaptive={true}
    transitionType="adaptive"
  >
    {children}
  </EnhancedQuestionTransitions>
);

export default EnhancedQuestionTransitions;